import type {EditMaterialOrder, MaterialOrder} from '@domain/material-storage/src/types'
import {ApiClient} from '@hconnect/apiclient'
import {type ErrorResponse} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {kebabCase} from 'lodash'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {notifyIfErrorMessage} from '../utils'

type Variables = {
  plantCode: string
  materialOrderId: number
  editedOrder: EditMaterialOrder
  property: keyof EditMaterialOrder
}

export const useEditMaterialOrder = (
  options?: Omit<
    UseMutationOptions<MaterialOrder, AxiosError<ErrorResponse>, Variables>,
    'mutationFn'
  >
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()
  return useMutation<MaterialOrder, AxiosError<ErrorResponse>, Variables>({
    ...options,
    mutationKey: ['editMaterialOrder'],
    mutationFn: async ({plantCode, materialOrderId, editedOrder, property}) => {
      const path = `/plants/${plantCode}/material-orders/${materialOrderId}/${kebabCase(property)}`
      const response = await ApiClient.axiosInstance.patch<MaterialOrder>(path, {
        [property]: editedOrder[property]
      })
      return response.data
    },
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.editMaterialOrder'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: ['materialOrders'], refetchType: 'all'})
      void queryClient.invalidateQueries({queryKey: ['stockDevelopment'], refetchType: 'all'})
    }
  })
}
